import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemIcon,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
  MatDivider,
} from '@angular/material/list';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { skipWhile, take } from 'rxjs/operators';

import { Cycle, Section } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { SectionItemDialogComponent } from '../section-item-dialog/section-item-dialog.component';

@Component({
  selector: 'app-section-overview',
  templateUrl: './section-overview.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    MatNavList,
    NgFor,
    MatListItem,
    MatIcon,
    MatListItemIcon,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatIconButton,
    MatListItemMeta,
    MatDivider,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class SectionOverviewComponent {
  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  cycle$: Observable<Cycle> = this.store.select(getSelectedCycle);

  constructor(
    private readonly store: Store<RootState>,
    private readonly dialog: DialogService,
    private readonly locale: LocaleService,
  ) {}

  addDialog() {
    this.store.dispatch(
      cycleActions.setSelectedSectionId({
        id: undefined,
      }),
    );
    this.dialog
      .form(SectionItemDialogComponent, {
        confirmButton: true,
        confirmButtonText: this.locale.translate('save'),
        pushHistoryState: false,
        sectionId: null,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.cycle$
            .pipe(
              take(1),
              skipWhile((cycle) => cycle === null || cycle === undefined),
            )
            .subscribe((cycle) => {
              const nextSectionId =
                cycle.sections && cycle.sections.length > 0
                  ? Math.max(...cycle.sections.map((d) => d.id)) + 1
                  : 1;
              this.store.dispatch(
                cycleActions.addSection({
                  cycleId: cycle.id,
                  section: { ...result.result.section, id: nextSectionId },
                }),
              );
            });
        }
      });
  }

  openSectionEdit(sectionId: number) {
    this.store.dispatch(
      cycleActions.setSelectedSectionId({
        id: sectionId,
      }),
    );
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      this.dialog
        .form(SectionItemDialogComponent, {
          confirmButton: true,
          confirmButtonText: this.locale.translate('save'),
          pushHistoryState: false,
          sectionId,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(
              cycleActions.editSection({ cycleId: cycle.id, section: result.result.section }),
            );
          }
          this.store.dispatch(
            cycleActions.setSelectedSectionId({
              id: undefined,
            }),
          );
        });
    });
  }

  delete(event: Event, section: Section) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      if (section && section.id) {
        this.store.dispatch(
          cycleActions.deleteSectionWithConfirmation({ cycleId: cycle.id, id: section.id }),
        );
      }
    });
  }

  sort(sections: Array<Section>): Array<Section> {
    return sections.slice().sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 0));
  }
}
